import { useCallback } from 'react';
import { Path, useFormContext } from 'react-hook-form';
import { useTranslation } from '@/i18n';
import { usePastedFields } from '@/contexts/PastedFieldsContext';
import { HotelFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { buildEmptyPensionCard } from '@/utils/benefits/hotel/buildEmptyPensionCard';
import {
  classifyPastedTariffs,
  findPastedOverlaps,
  matchPastedTariffs,
} from '@/utils/benefits/hotel/excelPaste/multiTariffPaste';
import { parsePensionTariffsBlock } from '@/utils/benefits/hotel/excelPaste/parsePensionTariffBlock';
import type { ChildBandsCount } from '@/utils/benefits/hotel/excelPaste/types';
import ExcelPastePanel from './ExcelPastePanel';
import { buildTariffNotes, ChildAges } from './pasteFormHelpers';
import {
  buildPensionRecap,
  getPastedPensionFieldNames,
  isBlankPensionCard,
  mergePastedPensionCard,
  PensionCard,
} from './pensionPasteHelpers';
import { waitForEffects } from './updateCategoryRoomOptions';
import { useExcelPasteFormatters } from './useExcelPasteFormatters';

interface PensionTariffsPasteProps {
  /** `commonPensionPricings` or `hotelRoomCategories.n.categoryPensionPricings`. */
  fieldArrayName: string;
  childBandsCount: ChildBandsCount;
  childAges: ChildAges;
  /** Replaces every pension card of the list (the field array's `replace`). */
  replaceCards: (cards: PensionCard[]) => void;
}

/**
 * Paste of every pension tariff at once: the 12×3 blocks side by side. Each
 * pasted tariff updates the card of the same label, fills a blank card or is
 * added; the other cards stay as they are.
 */
const PensionTariffsPaste = ({
  fieldArrayName,
  childBandsCount,
  childAges,
  replaceCards,
}: PensionTariffsPasteProps) => {
  const { t } = useTranslation();
  const { getValues } = useFormContext<HotelFormDataWithTranslations>();
  const { markPasted } = usePastedFields();
  const { formatIssue } = useExcelPasteFormatters(childAges);

  const tariffName = useCallback(
    (name: string | null | undefined, index: number) =>
      name || t('tariffs.tarif_', { index: index + 1 }),
    [t],
  );

  const parse = useCallback(
    (text: string) => {
      const cards = (getValues(
        fieldArrayName as Path<HotelFormDataWithTranslations>,
      ) ?? []) as PensionCard[];
      const basePensionType = getValues('pricingPensionType')?.value as
        | string
        | undefined;
      const result = parsePensionTariffsBlock(text, {
        childBandsCount,
        basePensionType,
      });
      const existing = cards.map((card) => ({
        name: card.name,
        isBlank: isBlankPensionCard(card),
      }));
      const plan = result.value
        ? matchPastedTariffs(result.value, existing)
        : null;
      return { cards, basePensionType, result, existing, plan };
    },
    [childBandsCount, fieldArrayName, getValues],
  );

  const analyze = useCallback(
    (text: string) => {
      const { cards, basePensionType, result, existing, plan } = parse(text);
      if (!result.value || !plan) {
        return {
          errors: result.errors.map(formatIssue),
          warnings: [],
          recap: [],
        };
      }

      const targetIndexes = new Set(
        plan.targets.map(({ tariffIndex }) => tariffIndex),
      );
      const warnings = findPastedOverlaps(
        plan.targets.map(({ pasted }) => pasted),
        cards
          .map((card, index) => ({
            name: tariffName(card.name, index),
            periods: card.periods ?? [],
          }))
          .filter((_, index) => !targetIndexes.has(index)),
      );

      return {
        errors: [],
        warnings: warnings.map(formatIssue),
        notes: buildTariffNotes(
          classifyPastedTariffs(plan.targets, existing, (index) =>
            tariffName(undefined, index),
          ),
          t,
        ),
        recap: plan.targets.flatMap(({ tariffIndex, pasted }) =>
          buildPensionRecap(
            cards[tariffIndex],
            pasted,
            { basePensionType, childBandsCount, childAges },
            t,
          ).map((row) => ({
            ...row,
            key: `${tariffIndex}-${row.key}`,
            label: `${pasted.name} · ${row.label}`,
          })),
        ),
      };
    },
    [childAges, childBandsCount, formatIssue, parse, t, tariffName],
  );

  const apply = useCallback(
    async (text: string) => {
      const { cards, plan } = parse(text);
      if (!plan) return;

      const nextCards = [
        ...cards,
        ...Array.from({ length: plan.newTariffCount }, buildEmptyPensionCard),
      ];
      plan.targets.forEach(({ tariffIndex, pasted }) => {
        nextCards[tariffIndex] = mergePastedPensionCard(
          nextCards[tariffIndex],
          pasted,
        );
      });
      replaceCards(nextCards);
      await waitForEffects();

      markPasted(
        plan.targets.flatMap(({ tariffIndex, pasted }) =>
          getPastedPensionFieldNames(
            `${fieldArrayName}.${tariffIndex}`,
            nextCards[tariffIndex],
            pasted,
          ),
        ),
      );
    },
    [fieldArrayName, markPasted, parse, replaceCards],
  );

  return (
    <ExcelPastePanel
      shape="pensionTariffs"
      title={t('hotels.pasteFromExcel.pensionTariffsButton')}
      analyze={analyze}
      apply={apply}
    />
  );
};

export default PensionTariffsPaste;
